import { useState } from 'react';
import _ from 'lodash';
import { Switch } from '@components/Inputs';
import { GridBreak } from '@components/Utils';
import { Paper, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Grid, TextInput, Select, Modal, Typography } from '@ts/storybook';

interface TableSettingsProps {
  tableSettings: any;
  refresh: any;
  sortColumn: string;
  sortOrder: string;
  useColumnsSelector: boolean;
  columns: any[];
  open: boolean;
  sortableColumns: any[];
  onSelectColumnToShow: (column: any) => void;
  onRemoveColumnToShow: (column: any) => void;
  onChangeSortColumn: (column: string) => void;
  onChangeSortOrder: (order: string) => void;
  onChangeAutoRefresh: () => void;
  onChangeRefreshTime: (event: any) => void;
  onBlurRefreshTime: (event: any) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const TableSettings = ({
  tableSettings,
  refresh,
  sortColumn,
  sortOrder,
  useColumnsSelector,
  columns,
  open,
  sortableColumns,
  onSelectColumnToShow,
  onRemoveColumnToShow,
  onChangeAutoRefresh,
  onChangeRefreshTime,
  onBlurRefreshTime,
  onCancel,
  ...props
}: TableSettingsProps) => {
  const { t } = useTranslation();
  const { minInterval, isEnabled } = refresh;
  const { refreshTime, autoRefresh } = tableSettings.refresh;
  const [tableSortColumn, setTableSortColumn] = useState(sortColumn);
  const [tableSortOrder, setTableSortOrder] = useState(sortOrder);

  const columnsOptionsDict = {
    //Component columns names, NOT the DB columns names
    createdAt: { display: t('Creation date') },
    customId: { display: t('Custom ID') },
    description: { display: t('Description') },
    dueDate: { display: t('Due date') },
    executionName: { display: t('Execution name') },
    licenseValidEnd: { display: t('License valid end') },
    licenseValidStart: { display: t('License valid start') },
    logicalId: { display: t('ID') },
    name: { display: t('Name') },
    notificationTime: { display: t('Notification time') },
    refEntityId: { display: t('Reason') },
    severity: { display: t('Severity') },
    status: { display: t('Status') },
    timestamp: { display: t('Timestamp') },
    unit: { display: t('Unit') },
    updatedAt: { display: t('Last modified') },
    userName: { display: t('Username') },
  };

  const sortOrderOptions = [
    { label: t('Descending'), value: 'DESC' },
    { label: t('Ascending'), value: 'ASC' },
  ];

  const onSelectColumn = selectedColumn => {
    const column = columns.find(col => col.name === selectedColumn.target.value);
    onSelectColumnToShow(column);
  };

  const getSortOptions = sortableColumns => {
    //build the options according to sortable columns
    const sortOptions: any[] = [];
    sortableColumns.forEach(sortable => {
      const column = columnsOptionsDict[sortable.field];
      // use always column as defined in settings (sortColumn)
      if (column)
        sortOptions.push({
          ...column,
          value: sortable.field,
        });
    });
    return sortOptions;
  };

  const getColumnsOptions = () => {
    //build the options according to sortable columns
    const columnOptions: any[] = [];
    const columnsToSelect = columns.filter(
      column => !tableSettings.columnsToShow.some(columnToShow => columnToShow.name === column.name)
    );
    columnsToSelect.forEach(column =>
      columnOptions.push({
        label: column.fnLabel ? column.fnLabel() : column.name,
        value: column.name,
      })
    );
    return columnOptions;
  };

  /** NEVER USED maybe in a future
   * Renders the columns selector if useColumnsSelector is true.
   *
   * @return {null|JSX.Element} The columns selector component or null if useColumnsSelector is false.
   */
  const renderColumnsSelector = () => {
    if (!useColumnsSelector) return null;
    return (
      <div>
        <Paper className="p-2.5 mt-4">
          <Typography variant="subtitle1" text="Columns to show" />
          {tableSettings.columnsToShow.map(columnToShow => (
            <Chip
              label={columnToShow.fnLabel ? columnToShow.fnLabel() : columnToShow.name}
              onDelete={() => onRemoveColumnToShow(columnToShow)}
              style={{ margin: 2 }}
            />
          ))}
        </Paper>
        <Select label={t('Select column to show')} options={getColumnsOptions()} value={''} onChange={onSelectColumn} />
      </div>
    );
  };

  const onChangeSortColumn = column => setTableSortColumn(column);

  const onChangeSortOrder = order => setTableSortOrder(order);

  const onConfirm = () => {
    props.onChangeSortColumn(tableSortColumn);
    props.onChangeSortOrder(tableSortOrder);
    props.onConfirm();
  };

  return (
    <Modal
      withHeader
      open={open}
      title="Table settings"
      onSubmit={onConfirm}
      onClose={onCancel}
      labelOk="Confirm"
      labelCancel="Cancel"
      fullWidth
      maxWidth="lg"
    >
      <Grid container spacing={2} p={4}>
        <Grid size={{ xs: 4 }}>
          <span style={{ fontSize: 20 }}>{t('Refresh')}</span>
        </Grid>
        <GridBreak />
        <Grid size={{ xs: 4 }}>
          <Switch checked={autoRefresh} disabled={!isEnabled} onChange={onChangeAutoRefresh} leftLabel={'Auto refresh'} />
        </Grid>
        <GridBreak />
        <Grid size={{ xs: 4 }}>
          <TextInput
            label={'Refresh interval in seconds' + ' (min ' + minInterval + ')'}
            value={refreshTime}
            onChangeValue={onChangeRefreshTime}
            disabled={!autoRefresh || !isEnabled}
            type="number"
            inputProps={{ min: minInterval, onBlur: onBlurRefreshTime }}
          />
        </Grid>
        <GridBreak />
        <Grid size={{ xs: 4 }}>
          <p>{`${t('Sorting by')}: ${sortColumn}, ${sortOrder}`}</p>
        </Grid>
        <GridBreak />
        <Grid size={{ xs: 4 }}>
          <Select
            label={t('Sort column')}
            options={getSortOptions(sortableColumns)}
            value={tableSortColumn}
            onChange={onChangeSortColumn}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Select label={t('Sort order')} options={sortOrderOptions} value={tableSortOrder} onChange={onChangeSortOrder} />
        </Grid>
        <GridBreak />
        {renderColumnsSelector()}
      </Grid>
    </Modal>
  );
};

export default TableSettings;
